'use client';

import type { MouseEvent, ReactNode } from 'react';
import { useLightbox } from './LightboxProvider';
import type { LightboxSlide } from './lightbox.types';
import { LIGHTBOX_INDEX_ATTR } from './lightbox.constants';

type LightboxTriggerProps = {
    slides: LightboxSlide[];
    children: ReactNode;
    className?: string;
};

/**
 * Client boundary for server-rendered galleries. The slides are built on the
 * server and the clicks are picked up by delegation, so the gallery markup
 * itself (images, captions, the Sanity image builder) never enters the client
 * bundle — only this listener and the slide data do.
 *
 * The `onClick` sits on a wrapper, but every trigger inside is a real
 * `<button data-lightbox-index="…">`: keyboard activation fires a click that
 * bubbles up here, so focus and Enter/Space keep working (see AGENTS.md →
 * Accessibility).
 */
export function LightboxTrigger({ slides, children, className }: LightboxTriggerProps) {
    const { openLightbox } = useLightbox();

    function handleClick(event: MouseEvent<HTMLDivElement>) {
        const target = event.target as HTMLElement | null;
        const trigger = target?.closest(`[${LIGHTBOX_INDEX_ATTR}]`);
        if (!trigger) return;

        const index = Number(trigger.getAttribute(LIGHTBOX_INDEX_ATTR));
        if (!Number.isInteger(index) || !slides[index]?.src) return;

        openLightbox({ slides, index });
    }

    return (
        <div className={className} onClick={handleClick}>
            {children}
        </div>
    );
}
